import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import Cookies from "js-cookie";
import api, { setAccessToken, clearAccessToken } from "../services/api";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Restore session                                                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (!token) {
      setLoading(false);
      return;
    }

    setAccessToken(token);

    let cancelled = false;

    api
      .get("/api/auth/me")
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        clearAccessToken();
        Cookies.remove("accessToken");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /* Login / Logout                                                      */
  /* ------------------------------------------------------------------ */
  const login = useCallback((userData, token) => {
    setUser(userData);
    setAccessToken(token);

    Cookies.set("accessToken", token, {
      secure: true,
      sameSite: "Strict"
    });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    clearAccessToken();
    Cookies.remove("accessToken");
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
